import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, TimeIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Input, InputGroup, InputRightAddon, Select, Text, useColorModeValue } from '@chakra-ui/react';
import currencySymbolMap from 'currency-symbol-map';
import moment from 'moment';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IoIosAddCircleOutline } from 'react-icons/io';
import BudgetTimeSelector from './BudgetTimeSelector';

function formatDate(dateObj) {
  if (!dateObj) return '';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(dateObj);
}

const UpdateBudgetScheduleSpecs = ({ specs, currency, setFieldValue, isReadOnly = true, onRemovePeriod, deletingIndex = '',  onPeriodChanged = () => {}, } ) => {

  const backgroundColor = useColorModeValue('gray.50', 'gray.700');
  const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
  const mainBackgroundColor = useColorModeValue('gray.100', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.200', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'inherit');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.700');
  const inputTextColor = useColorModeValue('#421e66', '#b5b5cd')

  const currencyCode = currency
  const currencySymbol = currencySymbolMap(currencyCode);

  const [expandedIndexes, setExpandedIndexes] = useState(specs.map(() => true));
  const [budgetValueInputs, setBudgetValueInputs] = useState(specs.map(p => p.budget_value));
  const [newUnsavedPeriods, setNewUnsavedPeriods] = useState([]);


  useEffect(() => {
    setBudgetValueInputs(specs.map(p => p.budget_value));
  }, [specs]);
  
  const handleToggleExpand = (index) => {
    const updated = [...expandedIndexes];
    updated[index] = !updated[index];
    setExpandedIndexes(updated);
  };

  const handleAddPeriod = () => {
    if (specs.length >= 50) return;
  
    const lastEndDate = specs.length > 0 
      ? moment.unix(specs[specs.length - 1].time_end) 
      : moment().add(1, 'day').startOf('day');
  
    const startDate = moment(lastEndDate).startOf('day');
    const endDate = moment(startDate).add(1, 'day').startOf('day');
  
    const newPeriod = {
      time_start: startDate.unix(),
      time_end: endDate.unix(),
      budget_value_type: 'ABSOLUTE',
      budget_value: 5,
      id: `temp-${Date.now()}`,
    };
  
    const updatedSpecs = [...specs, newPeriod];
    setFieldValue('budget_schedule_specs', updatedSpecs);
    setExpandedIndexes([...expandedIndexes, true]);
    // Update local new unsaved periods and get the new array
    setNewUnsavedPeriods((prev) => {
      const updatedNewPeriods = [...prev, newPeriod];
      
      // Notify parent with the full updated array of new unsaved periods
      onPeriodChanged(updatedNewPeriods);

      return updatedNewPeriods;
    });
  };  
  

  const handleRemovePeriod = (index) => {
    const removedPeriod = specs[index];
  
    const updatedSpecs = specs.filter((_, i) => i !== index);
    const updatedExpanded = expandedIndexes.filter((_, i) => i !== index);
    setFieldValue('budget_schedule_specs', updatedSpecs);
    setExpandedIndexes(updatedExpanded);
  
    setNewUnsavedPeriods((prev) => {
      const updatedNewPeriods = prev.filter(p => p.id !== removedPeriod.id);
  
      // Notify parent with the full updated array of new unsaved periods
      onPeriodChanged(updatedNewPeriods);
  
      return updatedNewPeriods;
    });
  };
  

  const updateStartTime = (startDate, time) => {
    return moment(startDate)
      .set({
        hours: parseInt(time.split(":"[0]), 10),
        minutes: parseInt(time.split(":"[1]), 10),
        seconds: 0,
      })
      .unix();
  };

  const updateEndTime = (endDate, time) => {
    return moment(endDate)
      .set({
        hours: parseInt(time.split(":"[0]), 10),
        minutes: parseInt(time.split(":"[1]), 10),
        seconds: 0,
      })
      .unix();
  };

  const timestampToDate = (ts) => ts ? new Date(ts * 1000) : null;
  const timestampToTimeString = (ts) => ts ? moment.unix(ts).format('HH:mm') : '';

  const isNewPeriod = (id) => newUnsavedPeriods.some(p => p.id === id);

  const handleChange = (index, field, value) => {
    // Update the main specs array
    const updatedSpecs = specs.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFieldValue('budget_schedule_specs', updatedSpecs);
  
    // If this period is new (unsaved), update newUnsavedPeriods as well
    const periodId = specs[index].id;
    if (isNewPeriod(periodId)) {
      setNewUnsavedPeriods((prev) => {
        // Update or add the changed period in newUnsavedPeriods
        const updatedNewPeriods = prev.map((p) =>
          p.id === periodId ? { ...p, [field]: value } : p
        );
  
        // If period wasn't already in newUnsavedPeriods, add it
        if (!prev.some(p => p.id === periodId)) {
          updatedNewPeriods.push({ ...specs[index], [field]: value });
        }
  
        // Notify parent with updated new unsaved periods
        onPeriodChanged(updatedNewPeriods);
  
        return updatedNewPeriods;
      });
    }
  };

  return (
    <Flex direction="column" gap={2}>
      {specs.map((period, index) => {
        const startDateObj = timestampToDate(period.time_start);
        const startTimeStr = timestampToTimeString(period.time_start);
        const endDateObj = timestampToDate(period.time_end);
        const endTimeStr = timestampToTimeString(period.time_end);

        return (
          <Box 
            key={index} 
            border="1px solid" 
            borderColor={borderColor} 
            p={expandedIndexes[index] ? 2 : 1} 
            px={2}
            borderRadius={6} 
            bg={mainBackgroundColor}  
            _hover={{ bg: hoverBgColor }} 
            mb={expandedIndexes[index] ? 2 : 0} 
          >
           <Button
              onClick={() => handleToggleExpand(index)}
              variant="none"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              pb={expandedIndexes[index] ? 1 : 0}
              px={0}
              size={'sm'}
            >
              <Text fontWeight={'normal'} textAlign="left">
                {expandedIndexes[index]
                  ? 'Time period for budget increase'
                  : `Spend ${currencySymbol}${parseFloat(period.budget_value) + 20} as the daily budget from ${formatDate(startDateObj)} to ${formatDate(endDateObj)}`}
              </Text>
              {expandedIndexes[index] ? <ChevronUpIcon boxSize={5}/> : <ChevronDownIcon boxSize={5}/>}
            </Button>

            {expandedIndexes[index] && (
              <Flex direction={'column'} px={4} pb={4} pt={2} bg={backgroundColor} gap={4} borderRadius={'sm'}>
                <Flex gap={1}>
                  <Flex direction="column" flex={'48%'}>
                    <Text mb={1} fontSize={'14px'} fontWeight={'bold'}>Starts on</Text>
                      <Flex align="center" gap={2} border={'1px solid'} bg={inputBackground} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius={'md'}>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} p={0.5}>
                          <DatePicker
                            disabled={isReadOnly && !isNewPeriod(period.id)}
                            selected={startDateObj}
                            onChange={(date) => {
                              const updatedTs = updateStartTime(date, startTimeStr || '00:00');
                              handleChange(index, 'time_start', updatedTs);
                            }}
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            customInput={
                              <input
                                type="text"
                                className="datepicker-input"
                                placeholder="Select Date"
                                style={{
                                  fontSize: '13px',
                                  padding: '4px',
                                  border: 'none',
                                  width: '100px',
                                  height: '18px',
                                  color: inputTextColor,
                                  backgroundColor: 'inherit',
                              }}
                              />
                            }
                          />
                        </Flex>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} gap={2} borderLeft={'1px solid'} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} p={0.5}>
                        <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                        <BudgetTimeSelector
                          value={startTimeStr}
                          onChange={(newTime) => {
                            const updatedTs = updateStartTime(startDateObj || new Date(), newTime);
                            handleChange(index, 'time_start', updatedTs);
                          }}
                          disabled={isReadOnly}
                        />
                        </Flex>
                      </Flex>
                  </Flex>
                  <Flex flex={'4%'} alignItems={'center'} justifyContent={'center'} mt={5}>
                    -
                  </Flex>
                  <Flex direction="column" flex={'48%'}>
                    <Text mb={1} fontSize={'14px'} fontWeight={'bold'}>Ends</Text>
                    <Flex align="center" gap={2} border={'1px solid'} bg={inputBackground} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius={'md'}>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} p={0.5}>
                          <DatePicker
                            disabled={isReadOnly && !isNewPeriod(period.id)}
                            selected={endDateObj}
                            onChange={(date) => {
                              const updatedTs = updateEndTime(date, endTimeStr || '00:00');
                              handleChange(index, 'time_end', updatedTs);
                            }}
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            customInput={
                              <input
                                type="text"
                                className="datepicker-input"
                                placeholder="Select Date"
                                style={{
                                  fontSize: '13px',
                                  padding: '4px',
                                  border: 'none',
                                  width: '100px',
                                  height: '18px',
                                  color: inputTextColor,
                                  backgroundColor: 'inherit',
                              }}
                              />
                            }
                          />
                        </Flex>
                        <Flex alignItems={'center'} justifyContent={'center'} flex={0.5} gap={2} borderLeft={'1px solid'} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} p={0.5}>
                        <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                        <BudgetTimeSelector
                          value={endTimeStr}
                          onChange={(newTime) => {
                            const updatedTs = updateEndTime(endDateObj || new Date(), newTime);
                            handleChange(index, 'time_end', updatedTs);
                          }}
                          disabled={isReadOnly && !isNewPeriod(period.id)}
                        />
                        </Flex>
                      </Flex>
                  </Flex>
                </Flex>

                <Flex gap={4} align="center">
                  <Flex direction="column" flex={0.7}>
                    <Select
                      size={'sm'}
                      borderRadius="md"
                      value={specs[0]?.budget_value_type}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const updated = specs.map((item) => ({
                          ...item,
                          budget_value_type: selectedValue,
                        }));
                        setFieldValue('budget_schedule_specs', updated);
                      }}
                      isDisabled={isReadOnly || index !== 0}
                    >
                      <option value="ABSOLUTE">Increase daily budget by value amount ($)</option>
                      <option value="MULTIPLIER">Increase daily budget by a percentage (%)</option>
                    </Select>
                  </Flex>

                  <Flex direction="column" flex={0.3}>
                    <InputGroup>
                      <Input
                          placeholder={currencySymbol}
                          type="number"
                          size={'sm'}
                          borderRadius="md"
                          isDisabled={isReadOnly && !isNewPeriod(period.id)}
                          value={budgetValueInputs[index]}
                          onChange={(e) => {
                            const updatedInputs = [...budgetValueInputs];
                            updatedInputs[index] = e.target.value;
                            setBudgetValueInputs(updatedInputs);
                          }}
                          onBlur={() => {
                            const inputVal = budgetValueInputs[index];
                            const parsed = parseFloat(inputVal);

                            if (!isNaN(parsed)) {
                              handleChange(index, 'budget_value', parsed);
                            } else {
                              const updatedInputs = [...budgetValueInputs];
                              updatedInputs[index] = specs[index].budget_value;
                              setBudgetValueInputs(updatedInputs);
                            }
                          }}
                        />
                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                            {currencyCode}
                        </InputRightAddon>
                    </InputGroup>
                  </Flex>
                </Flex>

                <Flex>
                  <Text fontSize={'13px'}>{`Meta will aim to spend an average of ${currencySymbol}${parseFloat(period.budget_value) + 20} a day (a ${currencySymbol}${parseFloat(period.budget_value)} increase) from ${formatDate(startDateObj)} to ${formatDate(endDateObj)}.`}</Text>
                </Flex>

                <Flex alignItems={'center'} justifyContent={'space-between'}>
                    <Button
                      leftIcon={<DeleteIcon />}
                      colorScheme="gray"
                      variant="solid"
                      size="sm"
                      border="1px solid"
                      borderColor="gray.300"
                      isLoading={deletingIndex === period?.id}
                      onClick={() => {
                        if (isNewPeriod(period.id)) {
                          handleRemovePeriod(index);        
                        } else {
                          onRemovePeriod(period?.id);       // remove saved period via parent
                        }
                      }}
                    >
                      Remove this period
                    </Button>
                </Flex>
              </Flex>
            )}
          </Box>
        );
      })}

      <Flex>
            {specs?.length < 50 && (
              <Flex justifyContent="space-between" width="100%">
               <Button
                leftIcon={<IoIosAddCircleOutline />}
                colorScheme="gray"
                variant="solid"
                size="sm"
                border="1px solid"
                borderColor="gray.300"
                onClick={handleAddPeriod}
              >
                  Add another time period
              </Button>
              <Text fontSize={'14px'}>{`${specs?.length}/50 time periods`}</Text>
              </Flex>
            )}
      </Flex>
    </Flex>
  );
};

export default UpdateBudgetScheduleSpecs;
